import { useEffect, useState } from 'react';
import { Alert, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { IOpoCenterPackaging, IOpoPackagingCenter, OrganPackaging } from '@nkr/models/opo-packaging';
import { centerSelectStyles } from '../../../case/styles';
import { SettingsPageWrapper } from '../styles';
import { Pill, SettingsTableCard } from '../table-styles';
import useOpoPackaging from '@nkr/store/opo/hooks/use-opo-packaging';
import SegmentedChoice from './segmented-choice';
import Select from 'react-select';

const packagingOptions = [
    { value: OrganPackaging.InHouse, label: 'In-House' },
    { value: OrganPackaging.Opo, label: 'OPO' }
];

const gpsOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' }
];

const isComplete = (row: IOpoCenterPackaging) => row.livingDonorPackaging != null && row.deceasedDonorPackaging != null && row.needsGpsServices != null;

const toComparable = (rows: IOpoCenterPackaging[]) => JSON.stringify(rows.map(x => [x.centerId, x.livingDonorPackaging, x.deceasedDonorPackaging, x.needsGpsServices]));

const getProfileMismatch = (row: IOpoCenterPackaging, center: IOpoPackagingCenter | undefined) => {
    if (!center || center.centerProfilePackaging == null || row.livingDonorPackaging == null) {
        return null;
    }

    if ((row.livingDonorPackaging == OrganPackaging.Opo) == center.centerProfileUsesThisOpo) {
        return null;
    }

    if (center.centerProfilePackaging == OrganPackaging.InHouse) {
        return 'Center profile says In-House';
    }

    return center.centerProfileUsesThisOpo ? 'Center profile says this OPO' : 'Center profile names another OPO';
};

const OpoPackaging = () => {
    const { data, loading, error, save } = useOpoPackaging();
    const [rows, setRows] = useState<IOpoCenterPackaging[]>([]);
    const [showIncomplete, setShowIncomplete] = useState(false);
    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        if (data) {
            setRows(data.rows);
            setShowIncomplete(false);
            setSaveError('');
        }
    }, [data]);

    if (loading || !data) {
        return error ? <Alert bsStyle='danger'>{error}</Alert> : null;
    }

    const canManage = data.canManage;
    const savedRows = data.rows.filter(x => !x.isSuggested);
    const isDirty = toComparable(rows) != toComparable(savedRows);
    const incompleteCount = rows.filter(x => !isComplete(x)).length;
    const centerLookup = (centerId: number) => data.centers.find(x => x.id == centerId);
    const listedIds = rows.map(x => x.centerId);
    const centerOptions = data.centers
        .filter(x => listedIds.indexOf(x.id) < 0)
        .map(x => ({ value: x.id, label: `${x.unosId} - ${x.name}` }));

    const updateRow = (centerId: number, changes: Partial<IOpoCenterPackaging>) => {
        setRows(rows.map(x => x.centerId == centerId ? { ...x, ...changes } : x));
    };

    const addCenter = (option: { value: number } | null) => {
        if (option) {
            setRows([...rows, { centerId: option.value, livingDonorPackaging: null, deceasedDonorPackaging: null, needsGpsServices: null, isSuggested: false }]);
        }
    };

    const removeCenter = (centerId: number) => {
        setRows(rows.filter(x => x.centerId != centerId));
    };

    const handleDiscard = () => {
        setRows(data.rows);
        setShowIncomplete(false);
        setSaveError('');
    };

    const handleSave = async () => {
        setSaveError('');

        if (incompleteCount > 0) {
            setShowIncomplete(true);
            return;
        }

        const result = await save({
            centers: rows.map(x => ({
                centerId: x.centerId,
                livingDonorPackaging: x.livingDonorPackaging,
                deceasedDonorPackaging: x.deceasedDonorPackaging,
                needsGpsServices: x.needsGpsServices
            }))
        });

        if (result.success) {
            toast.success('Packaging settings saved');
        } else {
            setSaveError(result.message);
        }
    };

    return (
        <SettingsPageWrapper style={{ maxWidth: 1100 }}>
            {saveError && (
                <Alert bsStyle='danger' onDismiss={() => setSaveError('')}>{saveError}</Alert>
            )}

            {!canManage && (
                <div className='settings-notice'>Only OPO administrators can change packaging settings.</div>
            )}

            {rows.some(x => x.isSuggested) && (
                <div className='settings-notice'>
                    These centers list your OPO as their kidney packager in their center profile. Answer the remaining questions and save to confirm them, or remove any that don't apply.
                </div>
            )}

            <SettingsTableCard>
                <div className='table-card-header'>
                    <h4>Kidney Packaging by Center</h4>
                    <span className='count'>{rows.length} {rows.length == 1 ? 'center' : 'centers'}</span>
                </div>

                <div className='table-card-body'>
                    <Table condensed>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Center</th>
                                <th style={{ textAlign: 'left' }}>Living Donor Packaging</th>
                                <th style={{ textAlign: 'left' }}>Deceased Donor Packaging</th>
                                <th style={{ textAlign: 'left' }}>NKR GPS Services</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => {
                                const center = centerLookup(row.centerId);
                                const mismatch = getProfileMismatch(row, center);
                                const flagged = showIncomplete && !isComplete(row);

                                return (
                                    <tr key={row.centerId} style={flagged ? { backgroundColor: '#FDECEA' } : undefined}>
                                        <td style={{ textAlign: 'left' }}>
                                            <div>
                                                <strong>{center?.alias}</strong>
                                                {row.isSuggested && (
                                                    <>{' '}<Pill $color='amber'>From center profile or StrongBox</Pill></>
                                                )}
                                            </div>
                                            <div className='muted'>{center?.name}</div>
                                        </td>
                                        <td style={{ textAlign: 'left' }}>
                                            <SegmentedChoice
                                                label={`Living donor packaging for ${center?.alias}`}
                                                value={row.livingDonorPackaging}
                                                options={packagingOptions}
                                                disabled={!canManage}
                                                onChange={value => updateRow(row.centerId, { livingDonorPackaging: value })}
                                            />
                                            {mismatch && (
                                                <div className='settings-hint' style={{ color: '#9A4B00' }}>{mismatch}</div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'left' }}>
                                            <SegmentedChoice
                                                label={`Deceased donor packaging for ${center?.alias}`}
                                                value={row.deceasedDonorPackaging}
                                                options={packagingOptions}
                                                disabled={!canManage}
                                                onChange={value => updateRow(row.centerId, { deceasedDonorPackaging: value })}
                                            />
                                        </td>
                                        <td style={{ textAlign: 'left' }}>
                                            <SegmentedChoice
                                                label={`NKR GPS services for ${center?.alias}`}
                                                value={row.needsGpsServices}
                                                options={gpsOptions}
                                                disabled={!canManage}
                                                onChange={value => updateRow(row.centerId, { needsGpsServices: value })}
                                            />
                                        </td>
                                        <td>
                                            {canManage && (
                                                <div className='btn-group'>
                                                    <button type='button' className='btn btn-default' onClick={() => removeCenter(row.centerId)}>Remove</button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}

                            {rows.length == 0 && (
                                <tr>
                                    <td colSpan={5}>No centers have been added yet{canManage ? '. Use the search below to add the centers you package kidneys for.' : ''}</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {canManage && (
                    <div style={{ padding: '12px 20px', borderTop: '1px solid #EEF1F5', maxWidth: 480 }}>
                        <Select
                            inputId='add-packaging-center'
                            options={centerOptions}
                            value={null}
                            onChange={addCenter}
                            placeholder='Add a center... (type to search)'
                            noOptionsMessage={() => 'No matching centers'}
                            menuPortalTarget={document.body}
                            styles={centerSelectStyles(false)}
                        />
                    </div>
                )}
            </SettingsTableCard>

            {canManage && (
                <div className='settings-card'>
                    <div className='settings-card-footer' style={{ borderTop: 'none', alignItems: 'center' }}>
                        <span style={{ marginRight: 'auto', fontSize: 13, color: showIncomplete && incompleteCount > 0 ? '#A3201A' : '#6B7A8F' }}>
                            {showIncomplete && incompleteCount > 0
                                ? `Answer every question for the highlighted ${incompleteCount == 1 ? 'center' : 'centers'} before saving.`
                                : (isDirty ? 'You have unsaved changes.' : 'All changes saved.')}
                        </span>
                        <button type='button' className='btn btn-primary' disabled={!isDirty} onClick={handleSave}>Save Changes</button>
                        {isDirty && (
                            <button type='button' className='btn' onClick={handleDiscard}>Discard</button>
                        )}
                    </div>
                </div>
            )}
        </SettingsPageWrapper>
    );
}

export default OpoPackaging;
